import { Box, Button, Grid, Popover, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useFormik } from "formik";
import { useState } from "react";
import Flex from "../Flex";
import StyledButtonV2 from "../app/StyledButtonV2";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Badge from "@mui/material/Badge";
import AsyncSelect from "../AsyncSelect";
import CustomSelect from "../CustomSelect";
import CustomDatePicker from "../CustomDatePicker";
import { FilterRecord } from "./filter-constant";
import type { DateRangePresetValue } from "./filter-util";
import { getComponent } from "./filter-util";

interface FilterBuilderV2Props<T> {
  filters: FilterRecord;
  onApply: (v: T) => void;
  prefillValues?: T;
  activeFilterCount?: number;
}

const BRAND_COLOR = "#104E84";

export const FilterBuilderV2 = <T,>({
  filters,
  onApply,
  prefillValues,
  activeFilterCount,
}: FilterBuilderV2Props<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const buildValues = (source?: T) =>
    Object.keys(filters).reduce((acc, key) => {
      const f = filters[key];
      if (f?.type === "date_range_preset" && f?.childKeys) {
        f.childKeys.forEach(
          (ck) => (acc[ck] = (source as any)?.[ck] ?? undefined)
        );
      } else {
        acc[key] = (source as any)?.[key] ?? undefined;
      }
      return acc;
    }, {} as any);

  const clearedValues = buildValues();

  const initialValues = prefillValues || buildValues(prefillValues);
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => onApply(values as T),
  });

  const closeFilter = () => setAnchorEl(null);

  const submitAndClose = () => {
    formik.submitForm();
    closeFilter();
  };

  const onChange = (key: string, value: any) => {
    formik.setFieldValue(key, value);
  };

  const resetField = (key: string) => {
    const f = filters[key];
    if (f?.type === "date_range_preset" && f?.childKeys) {
      f.childKeys.forEach((ck) => formik.setFieldValue(ck, undefined));
    } else {
      formik.setFieldValue(key, undefined);
    }
  };

  const clearAll = () => {
    formik.resetForm({ values: clearedValues });
    onApply(clearedValues);
  };

  return (
    <>
      <Badge
        badgeContent={
          activeFilterCount && activeFilterCount > 0 ? activeFilterCount : null
        }
        color="error"
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StyledButtonV2
          variant="outlined"
          sIcon={
            activeFilterCount && activeFilterCount > 0 ? (
              <FilterAltIcon />
            ) : (
              <FilterAltOutlinedIcon />
            )
          }
          label="Filter"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        />
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box
          sx={{
            width: 320,
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
          }}
        >
          {/* ─── Header ─── */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            px="16px"
            py="12px"
            sx={{
              borderBottom: "1px solid #E8EBEC",
            }}
          >
            <Typography fontSize={16} fontWeight={600} color="#212D30">
              Filters
            </Typography>
            <Button
              variant="text"
              onClick={clearAll}
              sx={{
                color: BRAND_COLOR,
                fontSize: 12,
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                p: "4px 8px",
              }}
            >
              Clear All
            </Button>
          </Flex>

          {/* ─── Filter Fields ─── */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: "12px",
              py: "6px",
            }}
          >
            {Object.entries(filters).map(([k, value]) => {
              const isPreset =
                value?.type === "date_range_preset" && value?.childKeys;
              const fieldValue = isPreset
                ? (value.childKeys as string[]).reduce(
                    (o, ck) => ({ ...o, [ck]: formik.values[ck] }),
                    {}
                  )
                : formik.values[k];
              const fieldOnChange = isPreset
                ? (
                    v: string | DateRangePresetValue | unknown[] | undefined
                  ) => {
                    if (
                      v &&
                      typeof v === "object" &&
                      !Array.isArray(v) &&
                      "date_range" in v
                    ) {
                      Object.entries(v).forEach(([key, val]) =>
                        onChange(key, val)
                      );
                    } else {
                      onChange(k, v);
                    }
                  }
                : (v: unknown) => onChange(k, v);

              return (
                <Box key={k} py="6px">
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mb="6px"
                  >
                    <Typography fontSize={12} fontWeight={500} color="#373D41">
                      {value?.label}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => resetField(k)}
                      sx={{
                        color: BRAND_COLOR,
                        fontSize: 10,
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: "auto",
                        p: "2px 8px",
                        lineHeight: 1.2,
                      }}
                    >
                      Reset
                    </Button>
                  </Flex>
                  {isPreset ? (
                    <Box>
                      <CustomSelect
                        value={
                          (fieldValue as DateRangePresetValue)?.date_range ??
                          undefined
                        }
                        options={value?.options ?? []}
                        onChange={(v) =>
                          fieldOnChange({
                            ...(fieldValue as DateRangePresetValue),
                            date_range: v ?? undefined,
                            startDate: undefined,
                            endDate: undefined,
                          })
                        }
                        endIcon={
                          <CalendarTodayOutlinedIcon
                            sx={{ fontSize: 18, color: "#596063" }}
                          />
                        }
                      />
                      {(fieldValue as DateRangePresetValue)?.date_range ===
                        "CUSTOM_RANGE" && (
                        <Grid container sx={{ mt: 1 }} spacing={1}>
                          <Grid item xs={12}>
                            <CustomDatePicker
                              label="Start Date"
                              value={
                                (fieldValue as DateRangePresetValue)
                                  ?.startDate ?? undefined
                              }
                              onChange={(val) =>
                                fieldOnChange({
                                  ...(fieldValue as DateRangePresetValue),
                                  startDate: val ?? undefined,
                                })
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <CustomDatePicker
                              label="End Date"
                              value={
                                (fieldValue as DateRangePresetValue)?.endDate ??
                                undefined
                              }
                              onChange={(val) =>
                                fieldOnChange({
                                  ...(fieldValue as DateRangePresetValue),
                                  endDate: val ?? undefined,
                                })
                              }
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  ) : value.type === "select" && value.onSearch ? (
                    <AsyncSelect
                      placeholder={value?.placeholder}
                      onSearch={value.onSearch}
                      name={k}
                      options={value?.options}
                      value={fieldValue}
                      onChange={(opt) => fieldOnChange(opt)}
                      enableFilterOption
                    />
                  ) : (
                    getComponent(
                      fieldValue ?? undefined,
                      fieldOnChange,
                      value.type,
                      value?.options,
                      value?.placeholder,
                      value?.onSearch,
                      k
                    )
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ─── Footer ─── */}
          <Flex
            justifyContent="center"
            alignItems="center"
            gap="12px"
            px="12px"
            py="12px"
            sx={{
              borderTop: "1px solid #E8EBEC",
            }}
          >
            <Button
              variant="text"
              onClick={() => {
                formik.resetForm();
                closeFilter();
              }}
              sx={{
                color: BRAND_COLOR,
                fontSize: 14,
                fontWeight: 500,
                textTransform: "none",
                width: 100,
                height: 32,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitAndClose}
              sx={{
                backgroundColor: BRAND_COLOR,
                color: "#FFFFFF",
                fontSize: 14,
                fontWeight: 500,
                textTransform: "none",
                width: 100,
                height: 32,
                borderRadius: "4px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#0D3F6B",
                  boxShadow: "none",
                },
              }}
            >
              Apply
            </Button>
          </Flex>
        </Box>
      </Popover>
    </>
  );
};
